import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EMITTER_CONSTANTS,
  KEY_RECOIL,
  STRIP_MANAGEMENT_THUMBNAIL_INFO,
  AUTO_EVENTS,
} from '@/shared/config';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import {
  BEAT_HR_TAB_ID,
  selectActiveTab,
  selectChangeStripData,
  selectEventStripsData,
  selectIsLoadingStrip,
  selectPageIndex,
  selectReloadStripManagement,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  selectStripFilter,
  useReportStore,
} from '@/entities/report';
import { getStripIndex, getStripPosition } from '@/entities/strip';
import { MAP_AUTO_EVENT_TYPE } from '../constants';

/**
 * Backs the strip grid of the Strip Management tab with its filtered, paged strip list and selection behaviour. Merges unsaved changes over the fetched strips, filters by active event tab, exclude-from-report and capture type, publishes the total, slices the current page, keeps the selected strip valid across page and thumbnail-size changes, and moves the selection with the arrow keys.
 *
 * @returns An object carrying the paged strips, selection state, thumbnail config and the selection handlers.
 */
const useStripEvents = () => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the strip tab page index into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const sizeThumbnailConfig = useReportStore((s) => s.sizeStripManagementThumbnailConfig);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the selected strip of the strip tab into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new selected strip, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Writes the multi-strip selection of the strip tab into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new selection array, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const setTotalStrip = useReportStore((s) => s.actions.setStripTotalStrip);
  const eventStripsData = useReportStore(selectEventStripsData(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const stripFilter = useReportStore(selectStripFilter(keyRecoil));
  const isReloadStripManagement = useReportStore(selectReloadStripManagement(keyRecoil));

  const selectionAreaWrapperRef = useRef<any>();
  const arrowKeyToPrevPage = useRef(false);
  const stripPosition = useRef({ x: 0, y: 0 });
  const lastSizeThumbnailConfig = useRef(sizeThumbnailConfig.name);
  const newStripFilter = useRef<any>(undefined);
  const lastStripFilter = useRef<any>(undefined);
  const [page, setPage] = useState<any>(undefined);
  const [selected, setSelected] = useState<Set<any>>(new Set());

  const eventStrips = useMemo(() => {
    const filterType = (MAP_AUTO_EVENT_TYPE as any)[activeButton];
    const mergeDate = _.map(eventStripsData, (event) => {
      const eventChange = _.find(changeStripData, (x) => x.id === event.id);
      if (eventChange) {
        return {
          ...event,
          ...eventChange,
        };
      }
      return event;
    });
    const filterStrips = _.filter(
      mergeDate,
      (event) =>
        (filterType?.includes(event.type) ||
          !filterType ||
          (activeButton as any) === AUTO_EVENTS) &&
        (stripFilter.isHideExcludeReport ? event?.originalInfo?.isIncludedToReport : true) &&
        (stripFilter.showCaptureType === 'ALL' ||
          (stripFilter.showCaptureType === 'AUTO' && event.isAutoCapture) ||
          (stripFilter.showCaptureType === 'MANUAL' && !event.isAutoCapture)),
    );
    if (stripFilter.isHideExcludeReport !== newStripFilter.current?.isHideExcludeReport) {
      newStripFilter.current = stripFilter;
    }
    return filterStrips;
  }, [activeButton, eventStripsData, changeStripData, stripFilter, isReloadStripManagement]);

  useEffect(() => {
    setTotalStrip(eventStrips.length);
  }, [eventStrips]);

  /**
   * Replaces the set of strip keys marked by the selection-area overlay. Stores the incoming set, which an effect then resolves into the selected strip objects.
   *
   * @param newSelected - The set of selection keys reported by the selection area.
   * @returns Nothing.
   */
  const setSelectedEvent = (newSelected: any) => {
    setSelected(newSelected);
  };

  useEffect(() => {
    const selectedArr = [...selected];
    const selectedStrips: any[] = [];
    _.forEach(selectedArr, (item) => {
      const id = item.split('-')[1];
      /**
       * Resolves one selection key back to the strip object behind it. Scans the filtered strip list for an id match, falling back to an empty list when no strips have loaded.
       *
       * @returns The matching strip, or undefined when none carries that id.
       */
      const selectedEvent = (eventStrips || []).find((strip) => strip.id === id);
      if (selectedEvent) {
        selectedStrips.push(selectedEvent);
      }
    });
    setSelectedMultipleStrip((prev) => {
      if (prev.length === 0 && selectedStrips.length === 0) {
        return prev;
      }
      return selectedStrips;
    });
  }, [selected, eventStrips]);

  /**
   * Selects the clicked strip in the grid. Updates the stored selection only when a different strip is clicked, and records its row and column position for arrow-key navigation.
   *
   * @param event - The strip object that was clicked.
   * @param index - Its position within the current page slice.
   * @returns Nothing.
   */
  const handleClickSelectEvent = useCallback((event: any, index: any) => {
    setSelectedStrip((prev) => {
      if (event?.id !== prev?.id) {
        stripPosition.current = getStripPosition(
          index,
          STRIP_MANAGEMENT_THUMBNAIL_INFO.stripPerRow,
        );
        return event;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    if (page) {
      setPageIndex((prev) => {
        if (prev.index !== page.index) {
          return { index: page.index };
        }
        return prev;
      });
    }
  }, [page]);

  const sliceStrips = useMemo(() => {
    const start = pageIndex.index * STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit;
    const end = start + STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit;
    let sliceData = eventStrips.slice(start, end);
    if (_.isEmpty(sliceData) && !_.isEmpty(eventStrips) && pageIndex.index > 0) {
      const start = (pageIndex.index - 1) * STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit;
      const end = start + STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit;
      sliceData = eventStrips.slice(start, end);
    }
    return sliceData;
  }, [eventStrips, pageIndex, sizeThumbnailConfig]);

  useEffect(() => {
    const start = pageIndex.index * STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit;
    const end = start + STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit;
    if (
      _.isEmpty(eventStrips.slice(start, end)) &&
      !_.isEmpty(eventStrips) &&
      pageIndex.index > 0
    ) {
      setPageIndex({ index: pageIndex.index - 1 });
    }
    setSelectedStrip((prev) => {
      const foundStripIndex = _.findIndex(eventStrips, { id: prev?.id });
      if (
        foundStripIndex > -1 &&
        (lastSizeThumbnailConfig.current !== sizeThumbnailConfig.name ||
          lastStripFilter.current?.isHideExcludeReport !==
            newStripFilter.current?.isHideExcludeReport)
      ) {
        lastSizeThumbnailConfig.current = sizeThumbnailConfig.name;
        lastStripFilter.current = { ...newStripFilter.current };
        const newPageIndex = Math.floor(
          foundStripIndex / STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit,
        );
        if (newPageIndex !== pageIndex.index) {
          setPage({ index: newPageIndex });
        }
        return eventStrips[foundStripIndex];
      }
      const findIndex = _.findIndex(sliceStrips, { id: prev?.id });
      const findEvent = sliceStrips[findIndex];
      if (!findEvent) {
        stripPosition.current = getStripPosition(0, STRIP_MANAGEMENT_THUMBNAIL_INFO.stripPerRow);
        return sliceStrips[0];
      }
      stripPosition.current = getStripPosition(
        findIndex,
        STRIP_MANAGEMENT_THUMBNAIL_INFO.stripPerRow,
      );
      return findEvent;
    });
  }, [eventStrips, pageIndex, sizeThumbnailConfig, sliceStrips]);

  useEffect(() => {
    /**
     * Moves the strip selection with the arrow keys while the beat and heart-rate tab is hovered. Resets the selection overlay, steps the row or column position, selects the strip at the new index, and emits the next or previous page event when the step falls outside the current page.
     *
     * @param event - The window keydown event.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (_.isEmpty(selectionAreaWrapperRef.current) || event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      event.preventDefault();
      const { key } = event;
      switch (key) {
        case 'ArrowRight': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y += 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_MANAGEMENT_THUMBNAIL_INFO.stripPerRow,
          );
          if (index > sliceStrips.length - 1) {
            stripPosition.current.y -= 1;
            emitter.emit(EMITTER_CONSTANTS.STRIP_MANAGEMENT_NEXT);
          } else {
            const event = sliceStrips[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y -= 1;
            }
          }
          break;
        }
        case 'ArrowLeft': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y -= 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_MANAGEMENT_THUMBNAIL_INFO.stripPerRow,
          );
          if (index < 0) {
            arrowKeyToPrevPage.current = true;
            stripPosition.current.y += 1;
            emitter.emit(EMITTER_CONSTANTS.STRIP_MANAGEMENT_PREV);
          } else {
            const event = sliceStrips[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y += 1;
            }
          }
          break;
        }
        case 'ArrowUp': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x -= 1;
          const event =
            sliceStrips[
              getStripIndex(stripPosition.current, STRIP_MANAGEMENT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x += 1;
          }
          break;
        }
        case 'ArrowDown': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x += 1;
          const event =
            sliceStrips[
              getStripIndex(stripPosition.current, STRIP_MANAGEMENT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x -= 1;
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sliceStrips, isLoadingStrip]);

  useUpdateEffect(() => {
    const { x: row, y: column } = stripPosition.current;
    const currentStripIndexInList = column + row * STRIP_MANAGEMENT_THUMBNAIL_INFO.prevStripPerRow;
    const currentEventIndex =
      currentStripIndexInList +
      pageIndex.index * STRIP_MANAGEMENT_THUMBNAIL_INFO.prevStripDisplayLimit;
    const newPage = Math.floor(
      currentEventIndex / STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit,
    );
    setPageIndex({ index: newPage < 0 ? 0 : newPage });
  }, [sizeThumbnailConfig]);

  return {
    selectionAreaWrapperRef,
    selectedStrip,
    eventStrips,
    isLoadingStrip,
    selectedMultipleStrip,
    sliceStrips,
    sizeThumbnailConfig,
    handleClickSelectEvent,
    setSelectedEvent,
  };
};

export default useStripEvents;
